import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { requestJson } from '../lib/api';

const SUPPLIER_COLORS = ['#1d7fe0', '#0f9d8a', '#8b5cf6', '#f59e0b', '#ef4444', '#64748b'];

const formatNumber = (value) => Number(value || 0).toLocaleString('fr-FR');

const formatDiopter = (value) => {
  const number = Number(value || 0);
  return `${number > 0 ? '+' : ''}${number.toFixed(2)}`;
};

const getSupplierColor = (suppliers, supplier) => {
  const index = suppliers.findIndex((item) => item.supplier === supplier);
  return SUPPLIER_COLORS[index % SUPPLIER_COLORS.length];
};

function CountCell({ value, color }) {
  return (
    <TableCell align="right" sx={{ fontWeight: value ? 800 : 400, color: value ? `${color}.main` : 'text.secondary' }}>
      {value}
    </TableCell>
  );
}

function StockMatrix({ gamme, threshold }) {
  const theme = useTheme();
  const { spheres, cylinders, cellsByKey, maxAvailable } = useMemo(() => {
    const map = new Map();
    gamme.cells.forEach((cell) => {
      const key = `${cell.sphere}|${cell.cylinder}`;
      const current = map.get(key) || { available: 0, reserved: 0 };
      map.set(key, { available: current.available + cell.available, reserved: current.reserved + cell.reserved });
    });
    const sphereValues = gamme.cells.map((cell) => cell.sphere);
    const minSphere = Math.min(...sphereValues);
    const maxSphere = Math.max(...sphereValues);
    const sphereList = [];
    for (let value = maxSphere; value >= minSphere - 0.001; value -= 0.25) sphereList.push(Math.round(value * 100) / 100);
    return {
      spheres: sphereList,
      cylinders: [...new Set(gamme.cells.map((cell) => cell.cylinder))].sort((a, b) => a - b),
      cellsByKey: map,
      maxAvailable: Math.max(1, ...[...map.values()].map((cell) => cell.available)),
    };
  }, [gamme]);

  return (
    <TableContainer>
      <Table size="small" sx={{ width: 'auto', mx: 'auto', '& td, & th': { borderBottom: 0, px: 0.75, py: 0.25 } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ color: 'text.secondary', fontSize: 11, textAlign: 'right' }}>Sph \ Cyl</TableCell>
            {cylinders.map((cylinder) => (
              <TableCell key={cylinder} align="center" sx={{ fontWeight: 800, fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
                {formatDiopter(cylinder)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {spheres.map((sphere) => (
            <TableRow key={sphere}>
              <TableCell sx={{ color: 'text.secondary', fontSize: 12, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {formatDiopter(sphere)}
              </TableCell>
              {cylinders.map((cylinder) => {
                const cell = cellsByKey.get(`${sphere}|${cylinder}`);
                if (!cell) {
                  return (
                    <TableCell key={cylinder} align="center" sx={{ color: 'divider' }}>
                      ·
                    </TableCell>
                  );
                }
                const { available } = cell;
                const state = available <= 0 ? 'error' : available < threshold ? 'warning' : null;
                const background = state
                  ? alpha(theme.palette[state].main, 0.16)
                  : alpha(theme.palette.primary.main, 0.12 + (available / maxAvailable) * 0.5);
                return (
                  <Tooltip
                    key={cylinder}
                    title={`Sph ${formatDiopter(sphere)} / Cyl ${formatDiopter(cylinder)} : ${available} dispo${cell.reserved ? `, ${cell.reserved} réservé(s)` : ''}`}
                  >
                    <TableCell align="center" sx={{ p: '0 !important' }}>
                      <Box
                        sx={{
                          minWidth: 48,
                          lineHeight: '22px',
                          borderRadius: 1,
                          fontSize: 12,
                          fontWeight: 800,
                          fontVariantNumeric: 'tabular-nums',
                          bgcolor: background,
                          color: state ? `${state}.main` : 'text.primary',
                        }}
                      >
                        {available}
                      </Box>
                    </TableCell>
                  </Tooltip>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function StockSupplierOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [selectedKey, setSelectedKey] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setData(await requestJson('/statistics/stock-suppliers'));
    } catch (err) {
      setError(err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const allSuppliers = useMemo(() => data?.suppliers || [], [data]);
  const suppliers = useMemo(
    () => allSuppliers.filter((item) => !supplierFilter || item.supplier === supplierFilter),
    [allSuppliers, supplierFilter]
  );
  const threshold = data?.threshold ?? 2;

  const selected = useMemo(() => {
    const [supplier, label] = selectedKey.split('\u0000');
    const found = suppliers.find((item) => item.supplier === supplier)?.gammes.find((gamme) => gamme.label === label);
    if (found) return { supplier, gamme: found };
    const first = suppliers.find((item) => item.gammes.length);
    return first ? { supplier: first.supplier, gamme: first.gammes[0] } : null;
  }, [selectedKey, suppliers]);

  const maxAvailable = Math.max(1, ...suppliers.map((item) => item.available));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!allSuppliers.length) return <Alert severity="info">Aucun verre de stock actif.</Alert>;

  const sumGammes = (gammes) => gammes.reduce((total, gamme) => {
    ['refs', 'stock', 'reserved', 'available', 'outOfStock', 'lowStock'].forEach((key) => {
      total[key] = (total[key] || 0) + gamme[key];
    });
    return total;
  }, {});
  const grandTotal = sumGammes(suppliers.flatMap((item) => item.gammes));
  const excludedQuantity = (data?.excluded || []).reduce((total, item) => total + item.quantity, 0);

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ sm: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          État actuel du stock · seuil d'alerte : {threshold} verres disponibles
        </Typography>
        <ToggleButtonGroup size="small" exclusive value={supplierFilter} onChange={(_event, value) => value !== null && setSupplierFilter(value)}>
          <ToggleButton value="" sx={{ fontWeight: 800, px: 2 }}>
            Tous
          </ToggleButton>
          {allSuppliers.map((item) => (
            <ToggleButton key={item.supplier} value={item.supplier} sx={{ fontWeight: 800, px: 2 }}>
              {item.supplier}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(auto-fit, minmax(320px, 1fr))' }, gap: 2 }}>
        {suppliers.map((item) => (
          <Card key={item.supplier} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: getSupplierColor(allSuppliers, item.supplier) }} />
                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                  {item.supplier}
                </Typography>
                {item.blocked && <Chip size="small" color="error" label="Commandes bloquées" sx={{ fontWeight: 800 }} />}
                <Chip
                  size="small"
                  color={item.outOfStock ? 'warning' : 'success'}
                  variant="outlined"
                  label={item.outOfStock ? `${item.outOfStock} réf. en rupture` : 'Aucune rupture'}
                  sx={{ fontWeight: 800 }}
                />
              </Stack>
              <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                <Typography variant="h3" sx={{ fontWeight: 950, lineHeight: 1 }}>
                  {formatNumber(item.available)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  verres disponibles · {formatNumber(item.stock)} en stock dont {formatNumber(item.reserved)} réservés
                </Typography>
              </Stack>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mt: 2 }}>
                {[
                  ['Références', item.refs, 'text.primary'],
                  ['Rupture', item.outOfStock, item.outOfStock ? 'error.main' : 'text.primary'],
                  ['Sous seuil', item.lowStock, item.lowStock ? 'warning.main' : 'text.primary'],
                ].map(([label, value, color]) => (
                  <Box key={label} sx={{ bgcolor: 'action.hover', borderRadius: 2, px: 1.25, py: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textTransform: 'uppercase' }}>
                      {label}
                    </Typography>
                    <Typography sx={{ fontWeight: 900, fontSize: 18, color }}>{value}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>
            Répartition des verres disponibles par gamme
          </Typography>
          <Stack spacing={1.25}>
            {suppliers.map((item) => {
              const color = getSupplierColor(allSuppliers, item.supplier);
              const gammes = item.gammes.filter((gamme) => gamme.available > 0);
              return (
                <Box key={item.supplier} sx={{ display: 'grid', gridTemplateColumns: '90px minmax(0, 1fr) 60px', gap: 1.5, alignItems: 'center' }}>
                  <Typography sx={{ fontWeight: 900, fontSize: 13 }}>{item.supplier}</Typography>
                  <Box sx={{ display: 'flex', height: 26, borderRadius: 1.5, overflow: 'hidden', bgcolor: 'action.hover' }}>
                    {gammes.map((gamme, index) => (
                      <Tooltip key={gamme.label} title={`${gamme.label} : ${gamme.available}`}>
                        <Box
                          sx={{
                            width: `${(gamme.available / maxAvailable) * 100}%`,
                            bgcolor: alpha(color, Math.max(0.35, 1 - index * 0.22)),
                            borderRight: '2px solid',
                            borderColor: 'background.paper',
                            color: '#fff',
                            fontSize: 11,
                            fontWeight: 800,
                            display: 'flex',
                            alignItems: 'center',
                            pl: 0.75,
                            overflow: 'hidden',
                          }}
                        >
                          {gamme.available >= 40 ? gamme.available : ''}
                        </Box>
                      </Tooltip>
                    ))}
                  </Box>
                  <Typography sx={{ fontWeight: 900, textAlign: 'right' }}>{formatNumber(item.available)}</Typography>
                </Box>
              );
            })}
          </Stack>
        </CardContent>
      </Card>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.35fr 1fr' }, gap: 2, alignItems: 'start' }}>
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', minWidth: 0 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              Détail par gamme
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Cliquer une gamme pour afficher sa matrice sphère × cylindre.
            </Typography>
            <TableContainer sx={{ mt: 1.5 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fournisseur / gamme</TableCell>
                    {['Réf.', 'Stock', 'Réservé', 'Dispo', 'Rupture', 'Sous seuil'].map((label) => (
                      <TableCell key={label} align="right">
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {suppliers.map((item) => {
                    const { gammes } = item;
                    const total = sumGammes(gammes);
                    return (
                      <Fragment key={item.supplier}>
                        <TableRow sx={{ bgcolor: 'action.hover', '& td': { fontWeight: 900 } }}>
                          <TableCell>{item.supplier}</TableCell>
                          <TableCell align="right">{total.refs || 0}</TableCell>
                          <TableCell align="right">{formatNumber(total.stock)}</TableCell>
                          <TableCell align="right">{total.reserved || 0}</TableCell>
                          <TableCell align="right">{formatNumber(total.available)}</TableCell>
                          <CountCell value={total.outOfStock || 0} color="error" />
                          <CountCell value={total.lowStock || 0} color="warning" />
                        </TableRow>
                        {gammes.map((gamme) => {
                          const isSelected = selected?.supplier === item.supplier && selected?.gamme.label === gamme.label;
                          return (
                            <TableRow
                              key={gamme.label}
                              hover
                              selected={isSelected}
                              onClick={() => setSelectedKey(`${item.supplier}\u0000${gamme.label}`)}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell sx={{ pl: 3.5 }}>{gamme.label}</TableCell>
                              <TableCell align="right">{gamme.refs}</TableCell>
                              <TableCell align="right">{gamme.stock}</TableCell>
                              <TableCell align="right">{gamme.reserved}</TableCell>
                              <TableCell align="right">{gamme.available}</TableCell>
                              <CountCell value={gamme.outOfStock} color="error" />
                              <CountCell value={gamme.lowStock} color="warning" />
                            </TableRow>
                          );
                        })}
                      </Fragment>
                    );
                  })}
                  {suppliers.length > 1 && (
                    <TableRow sx={{ '& td': { fontWeight: 950, borderTop: '2px solid', borderTopColor: 'divider' } }}>
                      <TableCell>Total</TableCell>
                      <TableCell align="right">{grandTotal.refs || 0}</TableCell>
                      <TableCell align="right">{formatNumber(grandTotal.stock)}</TableCell>
                      <TableCell align="right">{grandTotal.reserved || 0}</TableCell>
                      <TableCell align="right">{formatNumber(grandTotal.available)}</TableCell>
                      <CountCell value={grandTotal.outOfStock || 0} color="error" />
                      <CountCell value={grandTotal.lowStock || 0} color="warning" />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {excludedQuantity > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                Exclu des totaux : {data.excluded.map((item) => `${item.supplier} / ${item.label}`).join(', ')} ({formatNumber(excludedQuantity)} pièces techniques).
              </Typography>
            )}
          </CardContent>
        </Card>

        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', minWidth: 0 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              Matrice {selected ? `· ${selected.supplier}` : ''}
            </Typography>
            {selected && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {selected.gamme.label} — {selected.gamme.refs} réf., {selected.gamme.available} verres disponibles
                </Typography>
                <StockMatrix gamme={selected.gamme} threshold={threshold} />
                <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="error.main" sx={{ fontWeight: 800 }}>
                    ■ Rupture
                  </Typography>
                  <Typography variant="caption" color="warning.main" sx={{ fontWeight: 800 }}>
                    ■ Sous seuil
                  </Typography>
                  <Typography variant="caption" color="primary.main" sx={{ fontWeight: 800 }}>
                    ■ Disponible
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    · pas de référence
                  </Typography>
                </Stack>
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    </Stack>
  );
}
